import React, { useState } from 'react';
import { BaseComponentProps } from '../types/componentThemes';
import { Header } from './Header';
import { AdminNavigation, NavItem } from './AdminNavigation';
import elcapitLogo from '../assets/El CapIT_logo_top_left.png';

export interface AdminLayoutProps extends BaseComponentProps {
  /** Navigation items for the sidebar */
  navItems: NavItem[];
  /** Currently active navigation path */
  activePath?: string;
  /** Logo element to display in header */
  logo?: React.ReactNode;
  /** User name to display in header */
  userName?: string;
  /** User avatar element */
  userAvatar?: React.ReactNode;
  /** Extra content to display in the header (e.g., location selector) */
  headerContent?: React.ReactNode;
  /** Callback when logout is clicked */
  onLogout?: () => void;
  /** Callback when a nav item is clicked */
  onNavigate?: (path: string) => void;
  /** Main content to display */
  children: React.ReactNode;
}

/**
 * AdminLayout Component
 * 
 * A layout component for the admin portal with fixed header and sidebar navigation.
 * Provides consistent structure for all admin pages.
 * 
 * @example
 * ```tsx
 * <AdminLayout
 *   navItems={adminNavItems}
 *   activePath="/dashboard"
 *   logo={<Logo />}
 *   userName="Admin User"
 *   onLogout={handleLogout}
 *   onNavigate={handleNavigate}
 * >
 *   <YourPageContent />
 * </AdminLayout>
 * ```
 */
export const AdminLayout: React.FC<AdminLayoutProps> = ({
  navItems,
  activePath,
  logo,
  userName,
  userAvatar,
  headerContent,
  onLogout,
  onNavigate,
  children,
  className = '',
  style,
  testId,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div
      className={`min-h-screen bg-neutral-50 dark:bg-neutral-900 ${className}`}
      style={style}
      data-testid={testId}
    >
      {/* Header */}
      <Header
        logo={logo}
        userName={userName}
        userAvatar={userAvatar}
        onLogout={onLogout}
        testId={testId ? `${testId}-header` : undefined}
      >
        {headerContent}
      </Header>

      {/* Sidebar Navigation */}
      <AdminNavigation
        items={navItems}
        activePath={activePath}
        collapsed={sidebarCollapsed}
        onToggle={handleToggleSidebar}
        onNavigate={onNavigate}
        testId={testId ? `${testId}-sidebar` : undefined}
      />

      {/* Main content area */}
      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'ml-16' : 'ml-64'
        }`}
        data-testid={testId ? `${testId}-main` : undefined}
      >
        <div className="max-w-7xl mx-auto px-6 py-8 text-neutral-900 dark:text-neutral-100">
          {children}
        </div>
      </main>

      {/* Embossed El CapIT watermark — fixed bottom center, only on gym subdomains */}
      {window.location.hostname.split('.').length >= 3 && (
        <div
          className="fixed bottom-4 left-0 right-0 flex justify-center pointer-events-none select-none z-10"
          style={{ marginLeft: sidebarCollapsed ? '4rem' : '16rem' }}
        >
          <img
            src={elcapitLogo}
            alt="Powered by El CapIT"
            className="h-14 opacity-50"
            style={{ filter: 'none' }}
          />
        </div>
      )}
    </div>
  );
};
